import React from "react";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import { getCurrentUserId } from "../../selectors/userSelectors";
import { getIsWatchedByLessonId } from "../../selectors/lessonsUserSelectors";
import EquivalentLessonWarning from "./EquivalentLessonWarning";

const getLessonsUsers = (state) => state.entities.lessonsUsers;
const getCourses = (state) => state.entities.courses;
const getLessonFromProps = (_, props) => props.lesson;

const getAllLessonsUsersForCurrentUser = createSelector(
  getLessonsUsers,
  getCurrentUserId,
  (lessonsUsers, currentUserId) => {
    return Object.values(lessonsUsers).filter(
      (lessonsUser) => lessonsUser.userId === currentUserId
    );
  }
);

const getCurrentLessonUser = createSelector(
  getAllLessonsUsersForCurrentUser,
  getLessonFromProps,
  (lessonsUsers, lesson) => {
    if (!lesson?.id) return null;
    return lessonsUsers.find(
      (lessonsUser) => lessonsUser.lessonId === lesson.id
    );
  }
);

const getEquivalentLessonsUsers = createSelector(
  getAllLessonsUsersForCurrentUser,
  getLessonFromProps,
  (lessonsUsers, lesson) => {
    if (!lesson?.meta?.equivalentLessonIds || !Array.isArray(lesson.meta.equivalentLessonIds)) {
      return [];
    }

    return lessonsUsers.filter((lessonsUser) =>
      lesson.meta.equivalentLessonIds.includes(lessonsUser.lessonId)
    );
  }
);

const getOldestLessonsUser = createSelector(
  getCurrentLessonUser,
  getEquivalentLessonsUsers,
  (currentLessonUser, equivalentLessonsUsers) => {
    const allRelevantLessonsUsers = [];

    if (currentLessonUser) {
      allRelevantLessonsUsers.push(currentLessonUser);
    }

    if (equivalentLessonsUsers && equivalentLessonsUsers.length > 0) {
      allRelevantLessonsUsers.push(...equivalentLessonsUsers);
    }

    if (allRelevantLessonsUsers.length === 0) {
      return null;
    }

    return allRelevantLessonsUsers.reduce((oldest, current) => {
      if (!oldest) return current;
      if (!current) return oldest;

      const oldestDate = new Date(oldest.createdAt);
      const currentDate = new Date(current.createdAt);

      return currentDate < oldestDate ? current : oldest;
    }, null);
  }
);

const getWarningData = createSelector(
  getCurrentLessonUser,
  getOldestLessonsUser,
  getCourses,
  (currentLessonUser, oldestLessonsUser, courses) => {
    if (!currentLessonUser) {
      return { shouldShowWarning: false, courseName: null };
    }

    if (!oldestLessonsUser) {
      return { shouldShowWarning: false, courseName: null };
    }

    if (String(currentLessonUser.id) === String(oldestLessonsUser.id)) {
      return { shouldShowWarning: false, courseName: null };
    }

    const courseId = oldestLessonsUser.courseId;

    const course = courses[courseId];

    if (!course || !course.name) {
      return { shouldShowWarning: false, courseName: null };
    }

    return {
      shouldShowWarning: true,
      courseName: course.name,
    };
  }
);

function EquivalentLessonWarningContainer(props) {
  const { shouldShowWarning, courseName } = props;

  return (
    <EquivalentLessonWarning
      shouldShowWarning={shouldShowWarning}
      courseName={courseName}
    />
  );
}

function mapStateToProps(state, ownProps) {
  const { lesson } = ownProps;
  const watched = getIsWatchedByLessonId(state, lesson?.id);

  if (
    !watched ||
    !lesson?.meta?.equivalentLessonIds ||
    !Array.isArray(lesson.meta.equivalentLessonIds) ||
    lesson.meta.equivalentLessonIds.length === 0
  ) {
    return {
      shouldShowWarning: false,
      courseName: null,
    };
  }

  const warningData = getWarningData(state, { lesson });

  return {
    shouldShowWarning: warningData.shouldShowWarning,
    courseName: warningData.courseName,
  };
}

export default connect(mapStateToProps)(EquivalentLessonWarningContainer);
